import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    IconButton,
    Paper,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, KeyboardArrowDown as ExpandMoreIcon, KeyboardArrowUp as ExpandLessIcon, LocalOffer as DiscountIcon } from '@mui/icons-material';
import { getDiscountCodes, createDiscountCode, deleteDiscountCode } from '../api/discountCodeApi';
import { getPlans } from '../api/planApi';
import { getAffiliateById } from '../api/affiliateApi';
import { formatCurrency } from '../utils/currency';

const DiscountCodeManagement = ({ affiliateId }) => {
    const [codes, setCodes] = useState([]);
    const [plans, setPlans] = useState([]);
    const [paymentCurrency, setPaymentCurrency] = useState('EUR');
    const [openDialog, setOpenDialog] = useState(false);
    const [expandedRow, setExpandedRow] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [formData, setFormData] = useState({
        code: '',
        autoGenerateCode: true,
        type: 'percent',
        value: '',
        startDate: '',
        endDate: '',
        appliesToAllPlans: true,
        planId: ''
    });
    const [loading, setLoading] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [codeToDelete, setCodeToDelete] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (affiliateId) fetchCodes();
        if (affiliateId) fetchPlans();
        if (affiliateId) fetchAffiliateCurrency();
    }, [affiliateId]);

    const fetchCodes = async () => {
        try {
            const data = await getDiscountCodes(affiliateId);
            setCodes(Array.isArray(data) ? data : []);
        } catch (error) {
            showSnackbar('Failed to load discount codes', 'error');
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchPlans = async () => {
        try {
            const data = await getPlans(affiliateId);
            const filtered = Array.isArray(data)
                ? data.filter((plan) => Number(plan?.affiliateId) === Number(affiliateId) && plan?.active)
                : [];
            setPlans(filtered);
        } catch (error) {
            setPlans([]);
        }
    };

    const fetchAffiliateCurrency = async () => {
        try {
            const affiliate = await getAffiliateById(affiliateId);
            setPaymentCurrency(affiliate?.paymentCurrency || 'EUR');
        } catch (error) {
            setPaymentCurrency('EUR');
        }
    };

    const formatDiscountValue = (row) => (
        row.type === 'percent' ? `${row.value}%` : formatCurrency(row.value, paymentCurrency)
    );

    const handleOpenDialog = () => {
        const today = new Date().toISOString().split('T')[0];
        const nextMonth = new Date();
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        setFormData({
            code: '',
            autoGenerateCode: true,
            type: 'percent',
            value: '',
            startDate: today,
            endDate: nextMonth.toISOString().split('T')[0],
            appliesToAllPlans: true,
            planId: ''
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleSubmit = async () => {
        const { type, value, startDate, endDate, code, autoGenerateCode, appliesToAllPlans, planId } = formData;
        if (!value || !startDate || !endDate) {
            showSnackbar('Please fill in all required fields', 'error');
            return;
        }
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
            showSnackbar('Please enter a valid number', 'error');
            return;
        }
        if (type === 'percent' && (numValue <= 0 || numValue > 100)) {
            showSnackbar('Percent must be between 0 and 100', 'error');
            return;
        }
        if (type === 'fixed' && numValue <= 0) {
            showSnackbar('Amount must be positive', 'error');
            return;
        }
        if (new Date(endDate) <= new Date(startDate)) {
            showSnackbar('End date must be after start date', 'error');
            return;
        }
        if (!autoGenerateCode && !code.trim()) {
            showSnackbar('Enter a code or use auto-generate', 'error');
            return;
        }
        if (!appliesToAllPlans && !planId) {
            showSnackbar('Please select a plan', 'error');
            return;
        }

        setLoading(true);
        try {
            await createDiscountCode({
                affiliateId,
                type,
                value: numValue,
                startDate,
                endDate,
                ...(appliesToAllPlans ? {} : { planId: Number(planId) }),
                ...(autoGenerateCode ? {} : { code: code.trim().toUpperCase() })
            });
            showSnackbar('Discount code added');
            fetchCodes();
            handleCloseDialog();
        } catch (error) {
            const msg = error.response?.data?.error || 'Failed to add discount code';
            showSnackbar(msg, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteDiscountCode(id);
            showSnackbar('Discount code deleted');
            setExpandedRow(null);
            fetchCodes();
        } catch (error) {
            showSnackbar('Failed to delete', 'error');
        }
    };

    const handleDeleteClick = (id, e) => {
        if (e) e.stopPropagation();
        setCodeToDelete(id);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setCodeToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        if (!codeToDelete) return;
        await handleDelete(codeToDelete);
        handleDeleteCancel();
    };

    const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');
    const isExpired = (endDate) => new Date(endDate) < new Date();
    const getStatusLabel = (row) => {
        if (isExpired(row.endDate)) return 'Expired';
        if (new Date(row.startDate) > new Date()) return 'Upcoming';
        return 'Active';
    };
    const getStatusColor = (row) => {
        if (isExpired(row.endDate)) return 'error';
        if (new Date(row.startDate) > new Date()) return 'text.secondary';
        return 'success.main';
    };

    const toggleRow = (id) => {
        setExpandedRow((prev) => (prev === id ? null : id));
    };

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DiscountIcon /> Discount codes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create discount codes for customers to use at checkout. Discount can be a percentage or fixed amount ({paymentCurrency}).
            </Typography>

            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenDialog}
                sx={{ mb: 2 }}
            >
                Add discount code
            </Button>

            {codes.length === 0 ? (
                <Paper variant="outlined" sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">No discount codes yet. Add your first one.</Typography>
                </Paper>
            ) : isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {codes.map((row) => (
                        <Paper
                            key={row.id}
                            variant="outlined"
                            sx={{
                                overflow: 'hidden',
                                cursor: 'pointer',
                                bgcolor: expandedRow === row.id ? 'action.hover' : 'background.paper'
                            }}
                            onClick={() => toggleRow(row.id)}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                    <Typography variant="body1" fontWeight={600} noWrap>
                                        {row.code}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {formatDiscountValue(row)}
                                    </Typography>
                                </Box>
                                {expandedRow === row.id ? (
                                    <ExpandLessIcon color="action" />
                                ) : (
                                    <ExpandMoreIcon color="action" />
                                )}
                            </Box>
                            <Collapse in={expandedRow === row.id}>
                                <Box sx={{ px: 2, pb: 2, pt: 0, borderTop: 1, borderColor: 'divider' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="caption" color="text.secondary">Type</Typography>
                                            <Typography variant="body2">{row.type === 'percent' ? 'Percent' : `Fixed amount (${paymentCurrency})`}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="caption" color="text.secondary">Value</Typography>
                                            <Typography variant="body2">{formatDiscountValue(row)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="caption" color="text.secondary">Valid from</Typography>
                                            <Typography variant="body2">{formatDate(row.startDate)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="caption" color="text.secondary">Valid until</Typography>
                                            <Typography variant="body2">{formatDate(row.endDate)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="caption" color="text.secondary">Applies to</Typography>
                                            <Typography variant="body2">{row.plan?.name || 'All plans'}</Typography>
                                        </Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">Times used</Typography>
                                            <Typography variant="body2">{row.usageCount ?? 0}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">Status</Typography>
                                            <Typography variant="body2" sx={{ color: getStatusColor(row) }}>{getStatusLabel(row)}</Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={(e) => handleDeleteClick(row.id, e)}
                                            sx={{ alignSelf: 'flex-start', mt: 1 }}
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                </Box>
                            </Collapse>
                        </Paper>
                    ))}
                </Box>
            ) : (
                <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Code</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Value</TableCell>
                                <TableCell>Start</TableCell>
                                <TableCell>End</TableCell>
                                <TableCell>Applies to</TableCell>
                                <TableCell align="right">Used</TableCell>
                                <TableCell align="right">Status</TableCell>
                                <TableCell align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {codes.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell sx={{ fontWeight: 500 }}>{row.code}</TableCell>
                                    <TableCell>{row.type === 'percent' ? 'Percent' : `Amount (${paymentCurrency})`}</TableCell>
                                    <TableCell align="right">
                                        {formatDiscountValue(row)}
                                    </TableCell>
                                    <TableCell>{formatDate(row.startDate)}</TableCell>
                                    <TableCell>{formatDate(row.endDate)}</TableCell>
                                    <TableCell>{row.plan?.name || 'All plans'}</TableCell>
                                    <TableCell align="right">{row.usageCount ?? 0}</TableCell>
                                    <TableCell align="right">
                                        <Typography variant="caption" sx={{ color: getStatusColor(row) }}>{getStatusLabel(row)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="error" onClick={(e) => handleDeleteClick(row.id, e)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Add discount code</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <FormControlLabel
                            control={
                                <RadioGroup
                                    row
                                    value={formData.autoGenerateCode ? 'auto' : 'manual'}
                                    onChange={(e) =>
                                        setFormData((f) => ({ ...f, autoGenerateCode: e.target.value === 'auto' }))
                                    }
                                >
                                    <FormControlLabel value="auto" control={<Radio />} label="Generate code automatically" />
                                    <FormControlLabel value="manual" control={<Radio />} label="Enter code manually" />
                                </RadioGroup>
                            }
                            label=""
                        />
                        {!formData.autoGenerateCode && (
                            <TextField
                                label="Discount code"
                                value={formData.code}
                                onChange={(e) => setFormData((f) => ({ ...f, code: e.target.value }))}
                                placeholder="e.g. SAVE10"
                                size="small"
                                fullWidth
                            />
                        )}
                        <FormControl fullWidth size="small">
                            <FormLabel sx={{ mb: 0.5 }}>Discount type</FormLabel>
                            <RadioGroup
                                row
                                value={formData.type}
                                onChange={(e) => setFormData((f) => ({ ...f, type: e.target.value }))}
                            >
                                <FormControlLabel value="percent" control={<Radio />} label="Percent (%)" />
                                <FormControlLabel value="fixed" control={<Radio />} label={`Amount (${paymentCurrency})`} />
                            </RadioGroup>
                        </FormControl>
                        <TextField
                            label={formData.type === 'percent' ? 'Percent (0–100)' : `Amount (${paymentCurrency})`}
                            type="number"
                            value={formData.value}
                            onChange={(e) => setFormData((f) => ({ ...f, value: e.target.value }))}
                            inputProps={
                                formData.type === 'percent'
                                    ? { min: 0, max: 100, step: 0.5 }
                                    : { min: 0, step: 0.01 }
                            }
                            size="small"
                            fullWidth
                        />
                        <TextField
                            label="Valid from"
                            type="date"
                            value={formData.startDate}
                            onChange={(e) => setFormData((f) => ({ ...f, startDate: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            fullWidth
                        />
                        <TextField
                            label="Valid until"
                            type="date"
                            value={formData.endDate}
                            onChange={(e) => setFormData((f) => ({ ...f, endDate: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            fullWidth
                        />
                        <FormControl fullWidth size="small">
                            <FormLabel sx={{ mb: 0.5 }}>Applies to</FormLabel>
                            <RadioGroup
                                row
                                value={formData.appliesToAllPlans ? 'all' : 'specific'}
                                onChange={(e) =>
                                    setFormData((f) => ({
                                        ...f,
                                        appliesToAllPlans: e.target.value === 'all',
                                        planId: e.target.value === 'all' ? '' : f.planId
                                    }))
                                }
                            >
                                <FormControlLabel value="all" control={<Radio />} label="All plans" />
                                <FormControlLabel value="specific" control={<Radio />} label="Specific plan" />
                            </RadioGroup>
                        </FormControl>
                        {!formData.appliesToAllPlans && (
                            <FormControl fullWidth size="small">
                                <InputLabel id="discount-plan-select-label">Plan</InputLabel>
                                <Select
                                    labelId="discount-plan-select-label"
                                    value={formData.planId}
                                    label="Plan"
                                    onChange={(e) => setFormData((f) => ({ ...f, planId: e.target.value }))}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                maxHeight: 320
                                            }
                                        }
                                    }}
                                >
                                    {plans.map((plan) => (
                                        <MenuItem key={plan.id} value={plan.id}>
                                            {plan.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                        {loading ? 'Saving...' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-discount-dialog-title"
                aria-describedby="delete-discount-dialog-description"
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle id="delete-discount-dialog-title">Confirm delete</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-discount-dialog-description">
                        Are you sure you want to delete this discount code? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDeleteConfirm} autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default DiscountCodeManagement;
